import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, FileText, Coins, TrendingUp, ArrowRight, Calendar, Users, Sparkles, Infinity, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/PageHeader';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth.jsx';
import { SetupChecklistBanner } from '@/components/SetupChecklistBanner';
import { ServiceAreaStormsWidget } from '@/components/ServiceAreaStormsWidget';

const STATUS_BADGE = {
    new: 'bg-sky-50 text-sky-700 border-sky-200',
    contacted: 'bg-slate-50 text-slate-700 border-slate-200',
    quoted: 'bg-amber-50 text-amber-800 border-amber-200',
    won: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    lost: 'bg-red-50 text-red-700 border-red-200',
    draft: 'bg-slate-50 text-slate-700 border-slate-200',
    finalized: 'bg-sky-50 text-sky-700 border-sky-200',
    sent: 'bg-amber-50 text-amber-800 border-amber-200',
    viewed: 'bg-violet-50 text-violet-700 border-violet-200',
    signed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    declined: 'bg-red-50 text-red-700 border-red-200',
};

function KPI({ label, value, icon: Icon, hint, testId }) {
    return (
        <Card data-testid={testId}>
            <CardContent className="pt-5 pb-5">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
                        <div className="mt-1 text-3xl font-display tabular-nums text-slate-900">{value}</div>
                        {hint && <div className="text-xs text-slate-500 mt-0.5">{hint}</div>}
                    </div>
                    {Icon && (
                        <div className="h-10 w-10 rounded-lg bg-slate-900 text-amber-500 flex items-center justify-center">
                            <Icon className="h-5 w-5" strokeWidth={1.75} />
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
}

export default function DashboardPage() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth();

    useEffect(() => {
        api.get('/me/dashboard').then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <>
                <PageHeader title="Dashboard" subtitle="Welcome back — here's your snapshot." testId="dashboard-header" />
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">{[1,2,3,4].map((i) => <Skeleton key={i} className="h-24" />)}</div>
            </>
        );
    }

    const kpis = data?.kpis || {};
    const credits = data?.credits || { monthly: 0, topup: 0, total: 0 };
    const sub = data?.subscription || {};
    const tier = sub?.tier;
    const isUnlimited = !!credits.unlimited;
    const isActiveSub = !!credits.active;
    // Trialing = subscription is in Stripe's `trialing` status. `credits.active`
    // is already true (both 'active' and 'trialing' count as active) but we
    // want to show a distinct "your trial is running" banner.
    const isTrialing = sub?.status === 'trialing' || credits?.subscription_status === 'trialing';
    const noSub = !isActiveSub;
    // Days remaining in the current period (trial or paid). For a trialing
    // subscription this is the trial-end countdown.
    const daysLeftInPeriod = (() => {
        if (!sub?.current_period_end) return null;
        try {
            const end = new Date(sub.current_period_end);
            const diff = Math.ceil((end - new Date()) / (1000 * 60 * 60 * 24));
            return diff > 0 ? diff : 0;
        } catch { return null; }
    })();

    return (
        <>
            <PageHeader
                title={`Hello ${(user?.full_name || '').split(' ')[0] || ''}`}
                subtitle="Find leads, build accurate quotes, win more jobs."
                testId="dashboard-header"
                actions={
                    <>
                        <Button asChild className="bg-slate-900 hover:bg-slate-800 text-white font-semibold" data-testid="dashboard-find-leads-cta">
                            <Link to="/app/leads"><Search className="h-4 w-4 mr-1" /> Find leads</Link>
                        </Button>
                        <Button asChild className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="dashboard-new-quote-cta">
                            <Link to="/app/quotes"><FileText className="h-4 w-4 mr-1" /> Quotes</Link>
                        </Button>
                    </>
                }
            />

            {/* Subscription state banners */}
            {isTrialing && (
                <div className="mb-4 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 via-amber-50/70 to-amber-100/70 px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="dashboard-trial-banner">
                    <div className="flex items-center gap-3">
                        <Gift className="h-5 w-5 text-amber-700 shrink-0" />
                        <div>
                            <div className="text-sm font-semibold text-slate-900">7-day free trial active</div>
                            <div className="text-xs text-slate-700">
                                {daysLeftInPeriod !== null
                                    ? <><b>{daysLeftInPeriod}</b> day{daysLeftInPeriod === 1 ? '' : 's'} remaining — your card is charged automatically on day 8 unless you cancel.</>
                                    : <>You have full access to your {tier || 'plan'} features during the trial. Cancel anytime before the trial ends to avoid a charge.</>
                                }
                            </div>
                        </div>
                    </div>
                    <Button asChild size="sm" variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-100" data-testid="dashboard-trial-manage-cta">
                        <Link to="/app/billing">Manage billing <ArrowRight className="h-3 w-3 ml-1" /></Link>
                    </Button>
                </div>
            )}
            {noSub && (
                <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 flex items-center justify-between gap-3 flex-wrap" data-testid="dashboard-trial-exhausted-banner">
                    <div className="text-sm text-slate-900">
                        <b>Your account is locked.</b> Start your 7-day free trial to unlock lead lookups, AI estimates, and quotes.
                    </div>
                    <Button asChild size="sm" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold" data-testid="dashboard-trial-exhausted-upgrade-cta">
                        <Link to="/pricing">Start free trial <ArrowRight className="h-3 w-3 ml-1" /></Link>
                    </Button>
                </div>
            )}

            {/* First-time setup nudge — quietly disappears once the account
                has company info + pricing profile in place. The "magic" of the
                app depends on these being filled (otherwise PDFs show blank
                fields and costs use generic per-square defaults). */}
            <div className="mb-4">
                <SetupChecklistBanner />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {isUnlimited ? (
                    <Card data-testid="kpi-credits">
                        <CardContent className="pt-5 pb-5">
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="text-xs uppercase tracking-wide text-slate-500">Lead lookups</div>
                                    <div className="mt-1 text-3xl font-display tabular-nums text-slate-900 flex items-center gap-1">
                                        <Infinity className="h-7 w-7 text-amber-600" strokeWidth={2.5} />
                                        <span className="text-base text-slate-600 font-normal">Unlimited</span>
                                    </div>
                                    <div className="text-xs text-slate-500 mt-0.5 capitalize">{tier || 'crew'} plan</div>
                                </div>
                                <div className="h-10 w-10 rounded-lg bg-slate-900 text-amber-500 flex items-center justify-center">
                                    <Coins className="h-5 w-5" strokeWidth={1.75} />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <KPI
                        label="Lookups left"
                        value={credits.total}
                        icon={Coins}
                        hint={`${credits.monthly} monthly + ${credits.topup} top-up`}
                        testId="kpi-credits"
                    />
                )}
                <KPI label="Leads unlocked" value={kpis.total_leads_unlocked || 0} icon={Search} testId="kpi-leads" />
                <KPI label="Quotes sent" value={kpis.quotes_sent || 0} icon={FileText} testId="kpi-quotes-sent" />
                <KPI label="Pipeline value" value={`$${(kpis.pipeline_value || 0).toLocaleString('en-US')}`} icon={TrendingUp} testId="kpi-pipeline" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
                <Card className="lg:col-span-2">
                    <CardHeader className="flex flex-row justify-between items-center">
                        <CardTitle className="font-display">Recent leads</CardTitle>
                        <Button asChild variant="ghost" size="sm" data-testid="dashboard-leads-viewall">
                            <Link to="/app/leads">View all <ArrowRight className="h-3 w-3 ml-1" /></Link>
                        </Button>
                    </CardHeader>
                    <CardContent>
                        {(data?.recent_leads || []).length === 0 ? (
                            <div className="text-sm text-slate-500 border border-dashed rounded-lg p-6 text-center">
                                No leads yet. <Link to="/app/leads" className="text-slate-900 underline">Search a ZIP</Link> to find homeowners likely to need repairs.
                            </div>
                        ) : (
                            <ul className="divide-y">
                                {(data.recent_leads || []).map((l, i) => (
                                    <li key={i} className="py-3 flex items-center justify-between">
                                        <div>
                                            <div className="font-medium text-slate-900">{l.address || '—'}</div>
                                            <div className="text-sm text-slate-500">{l.city}, {l.state} {l.zip_code}</div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Badge variant="outline" className={STATUS_BADGE[l.status] || ''}>{l.status}</Badge>
                                            <Button asChild variant="ghost" size="sm"><Link to={`/app/leads/${encodeURIComponent(l.property_key)}`}>Open</Link></Button>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader>
                        <CardTitle className="font-display">Your plan</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-xs uppercase tracking-wide text-slate-500">Tier</div>
                        <div className="font-display text-xl text-slate-900 mt-0.5">{tier ? tier[0].toUpperCase() + tier.slice(1) : 'No active plan'}</div>
                        <div className="text-sm text-slate-500">{sub?.plan_key ? sub.plan_key.replace('_', ' ') : '—'}</div>
                        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                            <div>
                                <div className="text-slate-500">Seats</div>
                                <div className="font-medium">{sub?.seats_included || 0} + {sub?.extra_seats || 0}</div>
                            </div>
                            <div>
                                <div className="text-slate-500">Monthly credits</div>
                                <div className="font-medium tabular-nums">{sub?.monthly_credits || 0}</div>
                            </div>
                        </div>
                        <Button asChild className="mt-4 w-full bg-slate-900 hover:bg-slate-800 text-white" data-testid="dashboard-billing-cta">
                            <Link to="/app/billing">Manage billing</Link>
                        </Button>
                    </CardContent>
                </Card>
            </div>

            {/* Service-area storm intel — high-leverage widget for storm chasers.
                Shows the most-active 0.1° lat/lng buckets in the contractor's state
                over the last 90 days, with a CTA to knock the affected neighborhoods. */}
            <div className="mt-4">
                <ServiceAreaStormsWidget />
            </div>

            <Card className="mt-4">
                <CardHeader className="flex flex-row justify-between items-center">
                    <CardTitle className="font-display">Recent quotes</CardTitle>
                    <Button asChild variant="ghost" size="sm" data-testid="dashboard-quotes-viewall">
                        <Link to="/app/quotes">View all <ArrowRight className="h-3 w-3 ml-1" /></Link>
                    </Button>
                </CardHeader>
                <CardContent>
                    {(data?.recent_quotes || []).length === 0 ? (
                        <div className="text-sm text-slate-500 border border-dashed rounded-lg p-6 text-center">No quotes yet. Convert your first lead into a quote.</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead><tr className="text-left text-slate-500"><th className="py-2 font-medium">Quote #</th><th className="font-medium">Homeowner</th><th className="font-medium">Address</th><th className="font-medium">Status</th><th></th></tr></thead>
                            <tbody>
                                {(data?.recent_quotes || []).map((q) => (
                                    <tr key={q.id} className="border-t">
                                        <td className="py-2 font-mono text-xs">{q.quote_number}</td>
                                        <td>{q.homeowner_name || '—'}</td>
                                        <td className="text-slate-600">{q.property_address || '—'}</td>
                                        <td><Badge variant="outline" className={STATUS_BADGE[q.status] || ''}>{q.status}</Badge></td>
                                        <td className="text-right"><Button asChild variant="ghost" size="sm"><Link to={`/app/quotes/${q.id}`}>Open</Link></Button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </CardContent>
            </Card>
        </>
    );
}
